import { createAvatar } from '@dicebear/core';
import { openPeeps } from '@dicebear/collection';
import {
  uniqueNamesGenerator,
  adjectives,
  colors,
  animals,
} from 'unique-names-generator';

export const formatWalletAddress = (address: string, chars = 4): string => {
  if (!address) return '';
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
};

export const generateAvatarFromAddress = (
  address: string,
  size: number = 44
): string => {
  if (!address) return '';

  const avatar = createAvatar(openPeeps, {
    seed: address,
    size,
    backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
    backgroundType: ['gradientLinear'],
  });

  return avatar.toDataUri();
};

// Convert SVG avatar to PNG base64 for Unity
export const generateAvatarAsPngBase64 = async (
  address: string,
  size: number = 128
): Promise<string> => {
  if (!address) return '';

  const avatar = createAvatar(openPeeps, {
    seed: address,
    size,
    backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
    backgroundType: ['gradientLinear'],
  });

  const svgString = avatar.toString();

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    canvas.width = size;
    canvas.height = size;

    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      // Get PNG base64 without the "data:image/png;base64," prefix
      const pngBase64 = canvas.toDataURL('image/png').split(',')[1];
      resolve(pngBase64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load SVG image'));
    };

    // Convert SVG to data URI
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.src = url;
  });
};

export const generateNameFromAddress = (address: string): string => {
  if (!address) return 'Anonymous';

  const seed = parseInt(address.slice(2, 10), 16);

  const username = uniqueNamesGenerator({
    dictionaries: [adjectives, colors, animals],
    separator: ' ',
    length: 2,
    seed,
    style: 'capital',
  });

  return username;
};
